import { Application } from '@platform/client';
import { ProfessionalApplicationStatusEnum } from '@platform/client/schemas';
import { Route, Switch, useLocation } from 'wouter';
import { ApplicationStorageDataKeys, Step } from '../Application';
import { ExperienceHopeSelection } from '../final-details/ExperienceHopeSelection';
import { LumenSubmission } from '../final-details/lumen/LumenSubmission';
import { ExperienceDetailSelection } from '../profile/shared/ExperienceDetailSelection';
import { LocationSelection } from '../profile/shared/LocationSelection';
import { ProfilePictureUpload } from '../profile/shared/ProfilePictureUpload';
import { RepeatVolunteerSelection } from '../profile/shared/RepeatVolunteerSelection';
import { ResumeSelection } from '../profile/shared/ResumeSelection';
import { ImpactAreaSelection } from '../program/shared/ImpactAreaSelection';
import { ApplicationApproved } from '../result/shared/ApplicationApproved';
import { ApplicationRejected } from '../result/shared/ApplicationRejected';
import { ApplicationUnderReview } from '../result/shared/ApplicationUnderReview';
import { ApplicationWaitlisted } from '../result/shared/ApplicationWaitlisted';
import { SkillsImpactSelection } from '../skills/SkillsImpactSelection';
import { SkillsExperience } from '../skills/SkillsSelection';

enum LumenApplicationSteps {
  IMPACT_AREA_SELECTION = 'IMPACT_AREA_SELECTION',
  PREVIOUS_VOLUNTEER_EXPERIENCE_SELECTION = 'PREVIOUS_VOLUNTEER_EXPERIENCE_SELECTION',

  LOCATION_SELECTION = 'LOCATION_SELECTION',
  RESUME_SELECTION = 'RESUME_SELECTION',
  EXPERIENCE_SELECTION = 'EXPERIENCE_SELECTION',
  PHOTO_SELECTION = 'PHOTO_SELECTION',

  SKILLS_SELECTION = 'SKILLS_SELECTION',
  SKILL_IMPACT_SELECTION = 'SKILL_IMPACT_SELECTION',

  EXPERIENCE_HOPE_SELECTION = 'EXPERIENCE_HOPE_SELECTION',
  REFERRAL_SELECTION = 'REFERRAL_SELECTION',
}

const LumenApplicationStep: Record<LumenApplicationSteps, Step> = {
  [LumenApplicationSteps.IMPACT_AREA_SELECTION]: {
    progress: 80,
    component: ImpactAreaSelection,
    url: '/',
  },
  [LumenApplicationSteps.PREVIOUS_VOLUNTEER_EXPERIENCE_SELECTION]: {
    progress: 30,
    component: RepeatVolunteerSelection,
    url: '/previous-volunteer-experience-selection',
  },
  [LumenApplicationSteps.LOCATION_SELECTION]: {
    progress: 135,
    component: LocationSelection,
    url: '/location-selection',
  },
  [LumenApplicationSteps.RESUME_SELECTION]: {
    progress: 150,
    component: ResumeSelection,
    url: '/resume-selection',
  },
  [LumenApplicationSteps.EXPERIENCE_SELECTION]: {
    progress: 170,
    component: ExperienceDetailSelection,
    url: '/experience-selection',
  },
  [LumenApplicationSteps.PHOTO_SELECTION]: {
    progress: 190,
    component: ProfilePictureUpload,
    url: '/photo-selection',
  },
  [LumenApplicationSteps.SKILLS_SELECTION]: {
    progress: 235,
    component: SkillsExperience,
    url: '/skills-selection',
  },
  [LumenApplicationSteps.SKILL_IMPACT_SELECTION]: {
    progress: 275,
    component: SkillsImpactSelection,
    url: '/skill-impact-selection',
  },
  [LumenApplicationSteps.EXPERIENCE_HOPE_SELECTION]: {
    progress: 340,
    component: ExperienceHopeSelection,
    url: '/experience-hope-selection',
  },
  [LumenApplicationSteps.REFERRAL_SELECTION]: {
    progress: 385,
    component: LumenSubmission,
    url: '/referral-selection',
  },
} as const;

export const LumenApplicationRoot = () => {
  const [location, navigate] = useLocation();

  const goToNextStep = () => {
    const steps = Object.values(LumenApplicationSteps);
    const currentIndex = steps.findIndex((step) => {
      return LumenApplicationStep[step as LumenApplicationSteps].url === location;
    });
    const nextStep =
      currentIndex < steps.length - 1 ? steps[currentIndex + 1] : steps[currentIndex];
    const nextUrl = LumenApplicationStep[nextStep as LumenApplicationSteps].url;
    navigate(nextUrl);
    return nextStep as LumenApplicationSteps;
  };

  const goToPreviousStep = () => {
    const steps = Object.values(LumenApplicationSteps);
    const currentIndex = steps.findIndex(
      (step) => LumenApplicationStep[step as LumenApplicationSteps].url === location,
    );

    if (currentIndex === 0) {
      window.location.href = 'platform/auth/application-start?partner=lumen';
      return;
    }

    const previousStep = steps[currentIndex - 1];
    const previousUrl = LumenApplicationStep[previousStep as LumenApplicationSteps].url;
    navigate(previousUrl);
    return previousStep as LumenApplicationSteps;
  };

  const applicationData = Application.useGetProfessionalApplicationStatus();

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.waitlisted) {
    return <ApplicationWaitlisted programName='Lumen Cares Volunteer Sprints' />;
  }

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.rejected) {
    return <ApplicationRejected programName='Lumen Cares Volunteer Sprints' />;
  }

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.approved) {
    return <ApplicationApproved />;
  }

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.pending) {
    return <ApplicationUnderReview />;
  }

  return (
    <Switch>
      {Object.entries(LumenApplicationStep).map(([_key, value]) => {
        const { url, component: Component, progress } = value;
        return (
          <Route key={url} path={url}>
            <Component
              onBack={goToPreviousStep}
              onContinue={goToNextStep}
              progress={progress}
              dataKey={ApplicationStorageDataKeys.PROFESSIONAL_APPLICATION}
            />
          </Route>
        );
      })}
    </Switch>
  );
};
